import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

// Actions that require typing the database name (irreversible)
const DESTRUCTIVE = ["purge", "delete"];

export default function DeleteDatabaseModal({ target, onArchive, onDelete, onPurge, onCancel }) {
  const { t } = useTranslation();
  const isArchived = target?.isClosed;

  // "archive" | "purge" | "delete" | null
  const [pendingAction, setPendingAction] = useState(null);
  const [typedName, setTypedName] = useState("");
  const inputRef = useRef(null);

  // Escape key: go back a step or close
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (pendingAction) { setPendingAction(null); setTypedName(""); }
        else onCancel();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel, pendingAction]);

  // Reset when target changes
  useEffect(() => {
    setPendingAction(null);
    setTypedName("");
  }, [target]);

  // Focus the input as soon as the confirm step mounts
  useEffect(() => {
    if (pendingAction && DESTRUCTIVE.includes(pendingAction) && inputRef.current) {
      inputRef.current.focus();
    }
  }, [pendingAction]);

  if (!target) return null;

  const dbName = target.label;
  const nameMatches = typedName.trim() === dbName;

  // ─── Step 2 — Destructive confirmation (Purge / Delete permanently) ──────────
  if (pendingAction && DESTRUCTIVE.includes(pendingAction)) {
    const isPurge = pendingAction === "purge";

    const actionLabel = isPurge
      ? t("admin.purgeOption", { defaultValue: "Purge data" })
      : t("admin.deleteOption", { defaultValue: "Delete permanently" });

    const actionDesc = isPurge
      ? t("admin.purgeConfirmDesc", {
          defaultValue:
            "All records will be deleted from this table. The schema, indexes and settings will be preserved. This cannot be undone.",
        })
      : t("admin.deleteConfirmDesc", {
          defaultValue:
            "The table and every record inside it will be permanently dropped from the database. This cannot be undone.",
        });

    const goBack = () => { setPendingAction(null); setTypedName(""); };
    const confirm = () => {
      setPendingAction(null);
      setTypedName("");
      if (isPurge) onPurge();
      else onDelete();
    };

    return (
      <div className="dbModalOverlay" onClick={goBack}>
        <div className="dbModalCard dbModalConfirmCard" onClick={(e) => e.stopPropagation()}>
          <button
            className="drawer-close-btn drawer-close-btn-abs"
            onClick={goBack}
            aria-label="Close"
            title="Close"
          >
            <svg width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true">
              <line x1="1" y1="1" x2="7" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
              <line x1="7" y1="1" x2="1" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
            </svg>
          </button>

          <div className={`dbModalConfirmBadge ${isPurge ? "dbModalConfirmBadge--warn" : "dbModalConfirmBadge--danger"}`}>
            {isPurge ? "⚠" : "🗑"}
          </div>

          <div className="dbModalTitle">
            {t("admin.confirmActionTitle", { defaultValue: "Confirm action" })}
          </div>

          <div className="dbModalDbName">{dbName}</div>

          <div className={`dbModalConfirmBanner ${isPurge ? "dbModalConfirmBanner--warn" : "dbModalConfirmBanner--danger"}`}>
            <strong>{actionLabel}</strong>
            <p>{actionDesc}</p>
          </div>

          <div className="dbModalConfirmInputGroup">
            <label className="dbModalConfirmLabel">
              {t("admin.typeNameToConfirm", {
                dbName,
                defaultValue: `Type the database name to confirm:`,
              })}
              <span className="dbModalConfirmLabelName">{dbName}</span>
            </label>
            <input
              ref={inputRef}
              className={`dbModalConfirmInput${typedName && !nameMatches ? " dbModalConfirmInput--invalid" : ""}${nameMatches ? " dbModalConfirmInput--valid" : ""}`}
              type="text"
              value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && nameMatches) confirm(); }}
              placeholder={dbName}
              autoComplete="off"
              spellCheck={false}
              aria-label={`Type ${dbName} to confirm`}
            />
            {typedName && !nameMatches && (
              <span className="dbModalConfirmInputHint">
                {t("admin.nameMismatch", { defaultValue: "Name does not match." })}
              </span>
            )}
          </div>

          <div className="dbModalConfirmActions">
            <button type="button" className="dbModalCancel dbModalConfirmBack" onClick={goBack}>
              ← {t("admin.back", { defaultValue: "Go back" })}
            </button>
            <button
              type="button"
              className={`dbModalConfirmBtn ${isPurge ? "dbModalConfirmBtn--warn" : "dbModalConfirmBtn--danger"}`}
              disabled={!nameMatches}
              onClick={confirm}
            >
              {actionLabel}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Step 2 — Archive / Restore confirmation (reversible, simple confirm) ────
  if (pendingAction === "archive") {
    const goBack = () => setPendingAction(null);
    const confirm = () => { setPendingAction(null); onArchive(); };
    const archiveLabel = isArchived
      ? t("admin.restoreOption", { defaultValue: "Restore" })
      : t("admin.archiveOption", { defaultValue: "Archive" });
    const archiveDesc = isArchived
      ? t("admin.restoreConfirmDesc", {
          defaultValue: "The database will become visible to users again. No data is changed.",
        })
      : t("admin.archiveConfirmDesc", {
          defaultValue: "The database will be hidden from users. All data is preserved and can be restored at any time.",
        });

    return (
      <div className="dbModalOverlay" onClick={goBack}>
        <div className="dbModalCard dbModalConfirmCard" onClick={(e) => e.stopPropagation()}>
          <button
            className="drawer-close-btn drawer-close-btn-abs"
            onClick={goBack}
            aria-label="Close"
            title="Close"
          >
            <svg width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true">
              <line x1="1" y1="1" x2="7" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
              <line x1="7" y1="1" x2="1" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
            </svg>
          </button>

          <div className="dbModalConfirmBadge dbModalConfirmBadge--info">
            {isArchived ? "↩" : "⊘"}
          </div>

          <div className="dbModalTitle">
            {t("admin.confirmActionTitle", { defaultValue: "Confirm action" })}
          </div>

          <div className="dbModalDbName">{dbName}</div>

          <div className="dbModalConfirmBanner dbModalConfirmBanner--info">
            <strong>{archiveLabel}</strong>
            <p>{archiveDesc}</p>
          </div>

          <div className="dbModalConfirmActions">
            <button type="button" className="dbModalCancel dbModalConfirmBack" onClick={goBack}>
              ← {t("admin.back", { defaultValue: "Go back" })}
            </button>
            <button
              type="button"
              className="dbModalConfirmBtn dbModalConfirmBtn--info"
              onClick={confirm}
            >
              {archiveLabel}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Step 1 — Choose action ───────────────────────────────────────────────────
  return (
    <div className="dbModalOverlay" onClick={onCancel}>
      <div className="dbModalCard" onClick={(e) => e.stopPropagation()}>
        <button
          className="drawer-close-btn drawer-close-btn-abs"
          onClick={onCancel}
          aria-label="Close"
          title="Close"
        >
          <svg width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true">
            <line x1="1" y1="1" x2="7" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
            <line x1="7" y1="1" x2="1" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
          </svg>
        </button>

        <div className="dbModalIcon">{isArchived ? "📦" : "⚠"}</div>

        <div className="dbModalTitle">
          {isArchived
            ? t("admin.manageDatabaseTitle", { defaultValue: "Manage archived database" })
            : t("admin.removeDatabaseTitle", { defaultValue: "Remove database" })}
        </div>

        <div className="dbModalDbName">{dbName}</div>

        <div className="dbModalDesc">
          {isArchived
            ? t("admin.manageDatabaseDesc", { defaultValue: "This database is archived and hidden from users." })
            : t("admin.removeDatabaseDesc", { defaultValue: "How would you like to remove this database?" })}
        </div>

        <div className="dbModalOptions">
          {/* Archive ↔ Restore toggle */}
          <button
            type="button"
            className="dbModalOptionBtn dbModalOptionArchive"
            onClick={() => setPendingAction("archive")}
          >
            <span className="dbModalOptionIcon">
              {isArchived ? "↩" : "⊘"}
            </span>
            <span className="dbModalOptionBody">
              <span className="dbModalOptionTitle">
                {isArchived
                  ? t("admin.restoreOption", { defaultValue: "Restore" })
                  : t("admin.archiveOption", { defaultValue: "Archive" })}
                {!isArchived && (
                  <span className="dbModalRecommended">
                    {t("admin.recommended", { defaultValue: "Recommended" })}
                  </span>
                )}
              </span>
              <span className="dbModalOptionDesc">
                {isArchived
                  ? t("admin.restoreOptionDesc", {
                      defaultValue: "Makes the database accessible to users again. No data is changed.",
                    })
                  : t("admin.archiveOptionDesc", {
                      defaultValue: "Hides the database from users. All data is preserved and can be restored.",
                    })}
              </span>
            </span>
          </button>

          {/* Purge data — wipes rows, keeps schema */}
          <button
            type="button"
            className="dbModalOptionBtn dbModalOptionPurge"
            onClick={() => setPendingAction("purge")}
          >
            <span className="dbModalOptionIcon">⌫</span>
            <span className="dbModalOptionBody">
              <span className="dbModalOptionTitle">
                {t("admin.purgeOption", { defaultValue: "Purge data" })}
              </span>
              <span className="dbModalOptionDesc">
                {t("admin.purgeOptionDesc", {
                  defaultValue: "Deletes all records from the table. Schema, indexes and settings are kept intact.",
                })}
              </span>
            </span>
          </button>

          {/* Permanent delete — always available */}
          <button
            type="button"
            className="dbModalOptionBtn dbModalOptionDelete"
            onClick={() => setPendingAction("delete")}
          >
            <span className="dbModalOptionIcon">×</span>
            <span className="dbModalOptionBody">
              <span className="dbModalOptionTitle">
                {t("admin.deleteOption", { defaultValue: "Delete permanently" })}
              </span>
              <span className="dbModalOptionDesc">
                {t("admin.deleteOptionDesc", {
                  defaultValue: "Drops the table and removes all data. This cannot be undone.",
                })}
              </span>
            </span>
          </button>
        </div>

        <button type="button" className="dbModalCancel" onClick={onCancel}>
          {t("admin.cancel", { defaultValue: "Cancel" })}
        </button>
      </div>
    </div>
  );
}
